import * as React from 'preact/compat'
import Autosuggest from '../react-autosuggest/Autosuggest'
import Api from '../../api'
import { loadStylesheet } from '../../common/helpers'
import PopoverContainer from './PopoverContainer'
import type { JSX } from 'preact'

export interface IAWProps {
  url: string,
  name: string,
  type?: string,
  className?: string,
  placeholder?: string,
  value?: string
  fakeValue?: string,
  min?: string,
  onChange?: string | ((data: {
    value: string,
    label: string,
    [key: string]: string
  }) => void),
  containerClass?: string,
  clearOnSelect?: boolean
}

interface IAWState {
  suggestions: Array<any>,
  value: string,
  fakeValue: string,
  loading: boolean
}

export class AutosuggestValueEvent extends Event {
  public value: string
  public fakeValue: string

  constructor(value: string, fakeValue: string) {
    super('autosuggest-value');
    this.value = value
    this.fakeValue = fakeValue
  }
}

export default class AutosuggestWrapper extends React.Component<IAWProps, IAWState> {
  state: IAWState = {
    suggestions: [],
    value: this.props.value || '',
    fakeValue: this.props.fakeValue || '',
    loading: false
  }

  controller: AbortController;
  input = React.createRef<HTMLInputElement>()
  fakeInput = React.createRef<HTMLInputElement>()

  public componentWillMount() {
    // noinspection JSIgnoredPromiseFromCall
    loadStylesheet('autocomplete')
  }

  public componentDidMount() {
    this.input.current.addEventListener('autosuggest-value', (event: AutosuggestValueEvent) => {
      let value = event.value
      let label = event.fakeValue
      const suggestion = { value, label }
      if (typeof this.props.onChange === 'string' && typeof window[this.props.onChange] === 'function') {
        window[this.props.onChange](this.input.current, suggestion, true)
      }
      if (typeof this.props.onChange === 'function') {
        this.props.onChange(suggestion)
      }
      this.setState({ value, fakeValue: label })
    })
  }

  fetchSuggestions = ({ value: term, reason }) => {
    if (reason !== 'input-changed') {
      return
    }
    if (this.props.min && term.length <= this.props.min) {
      this.setState({
        suggestions: [],
        loading: false
      })
      return
    }

    if (typeof this.controller !== 'undefined') {
      this.controller.abort()
    }

    this.controller = new AbortController();
    const signal = this.controller.signal

    this.setState({
      loading: true
    })

    Api.post({
      url: this.props.url,
      body: { term },
      init: { signal }
    })
      .then(suggestions => {
        if (!Array.isArray(suggestions)) {
          return
        }

        this.setState({
          suggestions,
          loading: false
        })
      })
  }

  renderSuggestion = (suggestion: {
    label: string,
    value: string,
    poster?: string,
    release?: string,
    genres?: string,
    avatar?: string
  }) => {
    switch (this.props.type) {
      case 'game':
        return <div class='autosuggest-game__container'>
          <div class="autosuggest-game inner-suggestion">
            <span class="logo" style={{ backgroundImage: `url('${suggestion.poster}')` }}/>
            <p className="gameName">{suggestion.label}</p>
            <p className="gameInfo">{suggestion.release} | {suggestion.genres}</p>
          </div>
        </div>
      case 'user':
        return <div class="autosuggest-user inner-suggestion">
          <span class="avatar" style={{ backgroundImage: `url('${suggestion.avatar}')` }}/>
          <p class="fullName">{suggestion.label}</p>
          {suggestion.label !== suggestion.value && <p class="userName">{suggestion.value}</p>}
        </div>
      default:
        return <div class='autosuggest-simple inner-suggestion'>
          {suggestion.label}
        </div>
    }
  }

  getSuggestionValue = (suggestion: {
    label: string,
    value: string
  }) => {
    this.setState({
      value: suggestion.value
    })
    return suggestion.label
  }

  onChange = (event: JSX.TargetedKeyboardEvent<HTMLInputElement>, { newValue }) => {
    const newState: Partial<IAWState> = {
      fakeValue: newValue
    }

    if (!event.code || !['ArrowUp', 'ArrowDown'].includes(event.code)) {
      newState.value = newValue
    }

    this.setState(newState)
  }

  onSelect = (_event: React.FormEvent<any>, { suggestion }: {
    suggestion: {
      value: string,
      label: string
    }
  }) => {
    let value = suggestion.value
    let label = suggestion.label

    if (typeof this.props.onChange === 'string' && typeof window[this.props.onChange] === 'function') {
      window[this.props.onChange](this.input.current, suggestion, false, this)
    }
    if (typeof this.props.onChange === 'function') {
      this.props.onChange(suggestion)
    }

    if (this.props.clearOnSelect) {
      value = null
      label = null
    }

    this.setState({ value, fakeValue: label })
  }

  onHighlight = () => {
    // Do nothing
  }

  onSuggestionsClearRequested = () => {
    this.setState({
      suggestions: []
    })
  }

  renderSuggestionsContainer = ({ containerProps, children }) => (
    <PopoverContainer props={containerProps} input={this.fakeInput}>{children}</PopoverContainer>
  );

  render() {
    const { suggestions, value, fakeValue, loading } = this.state

    let className = this.props.className

    if (loading) {
      className += ' input-loading'
    }

    const inputProps = {
      className: className,
      placeholder: this.props.placeholder,
      value: fakeValue || '',
      onChange: this.onChange,
      name: this.props.name ? `autosuggest[${this.props.name}]` : '',
      'data-realname': this.props.name,
      'data-autosuggest-fakeValueField': true,
      ref: this.fakeInput
    }

    return <>
      <input type="hidden" name={this.props.name} value={value} ref={this.input} data-autosuggest-valueField={true}/>
      <Autosuggest
        suggestions={suggestions}
        getSuggestionValue={this.getSuggestionValue}
        inputProps={inputProps}
        onSuggestionsFetchRequested={this.fetchSuggestions}
        // @ts-ignore
        renderSuggestion={this.renderSuggestion}
        // @ts-ignore
        renderSuggestionsContainer={this.renderSuggestionsContainer}
        onSuggestionSelected={this.onSelect}
        onSuggestionHighlighted={this.onHighlight}
        onSuggestionsClearRequested={this.onSuggestionsClearRequested}
      />
    </>
  }
}
